import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addRoundItem,
  changeProtocolDeviation,
  getProtocol,
  getRound,
  type ProtocolView,
  removeRoundItem,
  requestProtocolChange,
  type RoundView,
  signProtocol,
  startRound,
} from '../../../api/safetyRounds';
import { planKeys, useChecklistLanguage } from './usePlanQueries';

export const roundKeys = {
  one: (id: number, language: string) => ['safety-rounds', 'rounds', id, language] as const,
};

export const useRoundQuery = (id: number) => {
  const language = useChecklistLanguage();
  // The page keeps its own answers while walking, so it is not fetched again behind its back
  return useQuery({
    queryKey: roundKeys.one(id, language),
    queryFn: () => getRound(id, language),
    refetchOnWindowFocus: false,
  });
};

export const useStartRoundMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (planId: number) => startRound(planId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: planKeys.all }),
  });
};

const useRoundMutation = <T>(id: number, mutationFn: (input: T, language: string) => Promise<RoundView>) => {
  const queryClient = useQueryClient();
  const language = useChecklistLanguage();
  return useMutation({
    mutationFn: (input: T) => mutationFn(input, language),
    onSuccess: (round) => queryClient.setQueryData(roundKeys.one(id, language), round),
  });
};

export const useAddRoundItemMutation = (id: number) =>
  useRoundMutation(id, (input: { categoryKey: string; text: string }, language) => addRoundItem(id, input, language));
export const useRemoveRoundItemMutation = (id: number) =>
  useRoundMutation(id, (itemId: number, language) => removeRoundItem(id, itemId, language));

export const protocolKeys = {
  one: (id: number, language: string) => ['safety-rounds', 'protocols', id, language] as const,
};

/** The protocol in the reader's language, or the one asked for in the PDF */
export const useProtocolQuery = (id: number, language?: string) => {
  const readerLanguage = useChecklistLanguage();
  const shown = language ?? readerLanguage;
  return useQuery({ queryKey: protocolKeys.one(id, shown), queryFn: () => getProtocol(id, shown) });
};

const useProtocolMutation = <T>(id: number, mutationFn: (input: T, language: string) => Promise<ProtocolView>) => {
  const queryClient = useQueryClient();
  const language = useChecklistLanguage();
  return useMutation({
    mutationFn: (input: T) => mutationFn(input, language),
    onSuccess: (view) => {
      queryClient.setQueryData(protocolKeys.one(id, language), view);
      queryClient.invalidateQueries({ queryKey: ['safety-rounds', 'rounds', id] });
      queryClient.invalidateQueries({ queryKey: planKeys.all });
    },
  });
};

export const useSignProtocolMutation = (id: number) =>
  useProtocolMutation(id, (input: { role: 'SAFETY_REP' | 'GENERAL_MANAGER'; confirmedRead?: boolean }, language) =>
    signProtocol(id, input, language),
  );
export const useChangeProtocolDeviationMutation = (id: number) =>
  useProtocolMutation(
    id,
    ({ deviationId, ...input }: { deviationId: number; responsibleId: number; dueDate: string }, language) =>
      changeProtocolDeviation(id, deviationId, input, language),
  );
export const useRequestChangeMutation = (id: number) =>
  useProtocolMutation(id, (comment: string, language) => requestProtocolChange(id, comment, language));
